import { DialogTitle } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, DialogContent, Stack } from '@mui/material';
import { cloneElement, memo, useCallback, useEffect } from 'react';
import { BrowseJobTitleList } from './components/browse-job-title-list';
import { SelectedTitles } from './components/selected-titles/selected-titles';
import { useJobTitleExpanderStore } from './store';
import * as Styled from './styles';
import * as SX from './sx';
import type { JobTitleExpanderProps as Props } from './types';

export const JobTitleExpander = memo(
  ({
    className,
    state: _state,
    uncontrolledElement,
    disabled,
    defaultOpen,
    submitTitle,
    size = 'md',
    onClose,
    onOpen,
    onClickUncontrolledElement,
    defaultValues,
    onSubmit,
    ...props
  }: Props) => {
    const defaultState = useBoolean(
      !!defaultOpen,
      useCallback<(v: boolean) => void>(
        (v) => {
          if (v) onOpen?.();
          else onClose?.();
        },
        [onOpen, onClose]
      )
    );
    const state = _state || defaultState;

    // Use store
    const { isOpen, selectedValues, setData, setIsOpen, isLoading } =
      useJobTitleExpanderStore();
    const userSelectedValues = selectedValues.filter(
      (v) => !defaultValues?.includes(v)
    );

    // Initialize default values
    useEffect(() => {
      if (defaultValues) {
        const { setSelectedValues } = useJobTitleExpanderStore.getState();
        setSelectedValues(defaultValues);
      }
    }, [defaultValues]);

    // Sync dialog state
    useEffect(() => {
      setIsOpen(state.value);
    }, [state.value, setIsOpen]);

    const onClickElement = useCallback(() => {
      if (!disabled) {
        state.onToggle();
        setIsOpen(!isOpen);
      }
      onClickUncontrolledElement?.();
    }, [disabled, state, onClickUncontrolledElement, setIsOpen, isOpen]);

    const handleClose = useCallback(() => {
      if (onClose) onClose();
      else state.onFalse();
      setData(undefined);
      setIsOpen(false);
    }, [onClose, state, setIsOpen, setData]);

    const handleSubmit = useCallback(() => {
      onSubmit?.(selectedValues);
      handleClose();
    }, [selectedValues, onSubmit, handleClose]);

    return (
      <>
        {uncontrolledElement &&
          cloneElement(uncontrolledElement, {
            ...uncontrolledElement.props,
            onClick: onClickElement,
          })}
        <Styled.Wrapper
          className={classnames('JobTitleExpander', className)}
          sx={SX.root}
          open={state.value}
          fullWidth
          maxWidth={size}
          onClose={handleClose}
        >
          <DialogTitle
            title="Include Job Titles by AI"
            icon="workspace"
            onClose={handleClose}
          />
          <DialogContent sx={SX.content}>
            <Stack sx={SX.leftSection}>
              <SelectedTitles {...props} />
            </Stack>

            <Stack sx={SX.rightSection}>
              <BrowseJobTitleList />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              children="Cancel"
              variant="text"
              size="small"
              onClick={handleClose}
            />

            <LoadingButton
              children={submitTitle ?? 'Add to filter'}
              variant="contained"
              color="primary"
              size="small"
              sx={SX.button}
              disabled={disabled || userSelectedValues.length === 0}
              loading={isLoading}
              onClick={handleSubmit}
            />
          </DialogActions>
        </Styled.Wrapper>
      </>
    );
  }
);

JobTitleExpander.displayName = 'JobTitleExpander';
